import authApi from '@api/authApi';
import orgApi from '@api/orgApi';
import IconLocation from '@assets/icons/location.svg?react';
import IconLogbook from '@assets/icons/logbook.svg?react';
import DefaultPage from '@components/defaultPage';
import MenuList from '@components/menuList';
import MenuListItem from '@components/menuListItem';
import Title from '@components/title';
import UserTile from '@components/userTile';
import useAuth from '@hooks/useAuth';
import { useTranslator } from '@i18n';
import { Group } from '@mantine/core';
import ColorSchemeToggle from '@routes/profile/-colorSchemeToggle';
import MaintenanceModeButton from '@routes/profile/-maintenanceModeButton';
import {
  IconBinaryTree,
  IconFileInvoice,
  IconLink,
  IconLogout,
  IconReceipt,
  IconSettings,
  IconUser,
  IconUsersGroup,
} from '@tabler/icons-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import isTruthy from '@util/isTruthy';

export const Route = createFileRoute('/profile/')({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslator();
  const { user, hasClaim } = useAuth();
  // Which of the two sections is worth offering depends on what the user actually administrates: a group
  // admin holds nothing in the owning organization and would find that list empty. Unexpanded on purpose -
  // the same cache useAuth reads, so this costs no request, and a parent walked to for its name is not
  // something the user can manage.
  const [orgs] = orgApi.all.useCache();
  const canSeeOrgs = hasClaim('orgs/read', 'any') && !!orgs?.some((org) => org.type !== 'group');
  const canSeeGroups = hasClaim('orgs/read', 'any') && !!orgs?.some((org) => org.type === 'group');
  const canSeeUsers = hasClaim('users/read', 'any');
  const canSeeLocations = hasClaim('locations/write', 'any');
  // Billing is its own claim: administrating an organization does not come with seeing what it is billed
  const canSeeBilling = hasClaim('orgs/billing', 'any') && !!orgs?.some((org) => org.type !== 'group');
  const canSeeBillingRuns = hasClaim('orgs/billing', 'global');
  const navigate = useNavigate();

  const links = [
    canSeeOrgs && (
      <MenuListItem key="orgs" to="/profile/orgs" icon={<IconBinaryTree size={16} />} label={t('orgs.link')} />
    ),
    canSeeGroups && (
      <MenuListItem key="groups" to="/profile/groups" icon={<IconUsersGroup size={16} />} label={t('groups.link')} />
    ),
    canSeeUsers && (
      <MenuListItem key="users" to="/profile/users" icon={<IconUser size={16} />} label={t('users.link')} />
    ),
    canSeeLocations && (
      <MenuListItem
        key="locations"
        to="/profile/locations"
        icon={<IconLocation size={16} />}
        label={t('locations.link')}
      />
    ),
    canSeeBilling && (
      <MenuListItem key="billing" to="/profile/billing" icon={<IconReceipt size={16} />} label={t('billing.link')} />
    ),
    canSeeBillingRuns && (
      <MenuListItem
        key="billingRuns"
        to="/profile/billing/runs"
        icon={<IconFileInvoice size={16} />}
        label={t('billingRuns.link')}
      />
    ),
    <MenuListItem key="logs" to="/profile/log" icon={<IconLogbook size={16} />} label={t('log.link')} />,
  ].filter(isTruthy);

  const buttons = [
    config.FEATURE_MAINTENANCE_MODE && <MaintenanceModeButton key="maintenance" />,
    config.FEATURE_LIGHT_MODE && <ColorSchemeToggle key="colorScheme" />,
  ];

  return (
    <Title title={t('profile.title')}>
      <DefaultPage
        css={{
          display: 'grid',
          alignContent: 'start',
          gap: 'var(--mantine-spacing-md)',
          paddingBottom: 50,
        }}
      >
        {user && (
          <UserTile
            user={user}
            css={{
              margin: 'var(--mantine-spacing-md) 0',
            }}
          />
        )}

        {links.length > 0 && <MenuList dense>{links}</MenuList>}

        {buttons.length > 0 && <MenuList dense>{buttons}</MenuList>}

        <MenuList dense>
          <MenuListItem to="/profile/settings" icon={<IconSettings size={16} />} label={t('settings.link')} />

          <MenuListItem
            onClick={() => {
              authApi.logout();
              void navigate({ to: '/', search: { locationId: undefined }, params: false });
            }}
            icon={<IconLogout size={16} />}
            label={t('common.logout')}
          />
        </MenuList>

        <Group
          pl="md"
          css={{
            fontSize: 'var(--mantine-font-size-sm)',
            color: 'var(--mantine-color-dimmed)',
          }}
        >
          <span>
            {config.VERSION} &copy; {config.BUILDTIME.getFullYear()}
          </span>

          <a
            href={`${config.BACKEND_WEBSITE}/changelog`}
            target="_blank"
            rel="noopener noreferrer"
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: 'inherit',
            }}
          >
            <IconLink size="1em" />
            {t('profile.changelog')}
          </a>
        </Group>
      </DefaultPage>
    </Title>
  );
}
